import React, { useState } from 'react';

const FAQS = [
  {
    q: 'How much does Etsy take per sale in 2026?',
    a: `For US sellers, Etsy takes approximately 10-15% of every sale once you stack
    the listing fee ($0.20), the 6.5% transaction fee on the total order including
    shipping, and the 3% + $0.25 payment processing fee. If your sale comes through
    Offsite Ads, that figure jumps to 22-30%. Our calculator shows you the exact
    number for your products at the top of the Snapshot — labeled "Etsy keeps."`,
  },
  {
    q: 'What is the Etsy transaction fee in 2026?',
    a: `Etsy charges a flat 6.5% transaction fee on every sale. Critically, this fee
    applies to the total order — including shipping the buyer pays. So a $30 item
    with $5 shipping is $2.28 in transaction fees, not $1.95. The 6.5% rate has
    been in effect since April 2022 (raised from 5%).`,
  },
  {
    q: 'Should I opt out of Etsy Offsite Ads?',
    a: `If your shop has earned under $10,000 in the trailing 12 months, you can opt
    out — and it's usually worth it for low-margin products. Sellers above $10K/yr
    are required to participate at 12% on attributed sales (sellers under who
    participate pay 15%). Use our Insights tab to see how an Offsite Ads hit would
    affect your specific products before deciding.`,
  },
  {
    q: 'Is Etsy or Shopify cheaper for small sellers?',
    a: `It depends on your monthly volume. Etsy has near-zero fixed cost but
    high per-sale fees (~10-15%). Shopify Basic costs $39/month but only ~3% per
    sale. The crossover is typically around 80-100 sales/month — below that, Etsy
    wins; above it, Shopify is cheaper. Use our Platforms tab to find the exact
    crossover for your product.`,
  },
  {
    q: 'What is a good profit margin for an Etsy seller?',
    a: `30%+ net margin (after all Etsy fees and material costs) is a healthy target
    across most categories. Industry estimates: Jewelry 38%, Home Decor 32%,
    Print on Demand 22%, Digital Downloads 75%. Below 15% is dangerously thin —
    one supplier price bump can wipe out profit.`,
  },
  {
    q: 'How do Etsy fees differ outside the US?',
    a: `Listing fee ($0.20) and transaction fee (6.5%) are universal, but payment
    processing varies dramatically. UK sellers pay 4% + £0.20. Germany pays
    4% + €0.30. Some countries (UK, France, Italy, Spain) also pay a regulatory
    operating fee — typically 0.3% to 1%. Our calculator supports 61 countries
    with verified rates, plus a separate field for the Jun 22, 2026 regulatory
    fee changes.`,
  },
  {
    q: 'Can Etsy raise fees without notice?',
    a: `Yes — and they have. The 2022 transaction fee raise from 5% to 6.5% caught
    many sellers off guard. The Jun 22, 2026 regulatory fee changes affect 8
    countries. Our Updates tab tracks all known fee changes and includes a stress
    test slider so you can see how your products would handle a future hike.`,
  },
  {
    q: 'Should I offer free shipping on Etsy?',
    a: `Free shipping doesn't change your fee math directly — Etsy charges the same
    transaction fee whether shipping is separate or baked into the price. However,
    Etsy's algorithm boosts free-shipping listings in search results, which can
    meaningfully lift conversion. The trade-off: if you raise prices to absorb
    shipping, the higher item price may slightly reduce conversion. For most
    sellers, free shipping wins.`,
  },
];

export default function FAQSection() {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <section
      id="faq"
      style={{
        padding: '3rem 1rem 2rem',
        background: 'rgba(180, 83, 9, 0.03)',
      }}
    >
      <div style={{ maxWidth: '52rem', margin: '0 auto' }}>
        <h2
          style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: '2.2rem',
            fontWeight: 700,
            color: '#1a1a2e',
            marginBottom: '0.5rem',
            letterSpacing: '-0.01em',
          }}
        >
          ❓ Frequently asked questions
        </h2>
        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '1rem',
            color: 'rgba(26, 26, 46, 0.82)',
            marginBottom: '2rem',
            lineHeight: 1.55,
          }}
        >
          The questions every Etsy seller eventually asks — answered with current 2026 fees.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {FAQS.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <div
                key={i}
                style={{
                  background: '#fefdf8',
                  border: isOpen ? '2px solid #1e3a8a' : '1.5px solid rgba(26, 26, 46, 0.1)',
                  borderRadius: '0.625rem',
                  transition: 'all 0.15s',
                }}
              >
                <button
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    padding: '0.85rem 1rem',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#1a1a2e',
                  }}
                >
                  <span>{item.q}</span>
                  <span
                    style={{
                      fontSize: '1.2rem',
                      color: '#1e3a8a',
                      transition: 'transform 0.2s',
                      transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                      flexShrink: 0,
                    }}
                  >
                    +
                  </span>
                </button>
                {isOpen && (
                  <div
                    style={{
                      padding: '0 1rem 1rem',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '1rem',
                      color: 'rgba(26, 26, 46, 0.78)',
                      lineHeight: 1.65,
                    }}
                  >
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
